import { useState, useEffect } from 'react';
import DocumentTypeSelect from './DocumentTypeSelect';
import InsuranceCompanySelect from './InsuranceCompanySelect';
import ProductTypeSelect from './ProductTypeSelect';
import DateRangePicker from './DateRangePicker';
import DatePicker from './DatePicker';
import { DOCUMENT_TYPE, getInsuranceTypeName, getInsuranceTypeFromTabName } from '../utils/insuranceTypeUtils';
import { getOrderedDocumentTypes, normalizeDocumentTypes, getCompaniesForDocumentTypeAll, hasEnabledNonDocumentTypeAll } from '../utils/documentTypeUtils';
import { isFeatureEnabledDocumentTypeAll } from '../utils/featureFlags';
import useRequireAuth from '../hooks/useRequireAuth';
import { fetchDocumentTypesAccess } from '../services/api/DocumentTypeService';
import { fetchInsuranceCompanies, fetchAgencyInsuranceCompanies } from '../services/api/InsuranceService';

const HistorySearchSidebar = ({
  isMobile,
  sidebarMinimized,
  sidebarActive,
  toggleSidebarMinimized,
  searchParams,
  setSearchParams,
  onSearch,
  onReset,
  isLoading,
}) => {

  const [activeTab, setActiveTab] = useState('life');

  // 権限に応じたアクセス可能な文書種別一覧
  const [documentTypesAccess, setDocumentTypesAccess] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [selectedInsuranceCompanies, setSelectedInsuranceCompanies] = useState([]);
  const [selectedProductTypes, setSelectedProductTypes] = useState([]);

  const userInfo = useRequireAuth();

  /** タブ切り替え処理 */
  const onTabChange = (tabName) => {
    setActiveTab(tabName);
    handleReset(getInsuranceTypeFromTabName(tabName));
  };

  useEffect(() => {
    const fetchAccessDocumentTypes = async () => {
      if (!userInfo) return;
      try {
        const response = await fetchDocumentTypesAccess();
        setDocumentTypesAccess(normalizeDocumentTypes(response));
      } catch (error) {
        // エラーダイアログ表示はBaseApiServiceで行う
        // エラーコンソール出力は各ApiServiceで行う
      }
    }
    fetchAccessDocumentTypes();
  }, [userInfo, activeTab]);

  useEffect(() => {
    // タブ切り替え時に保険会社の選択肢を再取得する（管理者は全件、代理店は許可された文書種別に基づいて絞り込み）
    const fetchSetInsuranceCompanies = async () => {
      try {
        if (!userInfo) {
          return;
        }

        const isAdmin = userInfo.is_admin;
        // 管理者で募集人サイトログイン時のみ、引受目安・ハンドブックを絞り込み
        // しおり・約款 / 新契約関連 / 保全・収納・支払関連 は全社表示
        const docTypeKey = mapDocumentTypeToKey(searchParams.documentType);
        const shouldFilterForAdmin = isAdmin && (docTypeKey === 'hikiuke' || docTypeKey === 'handbook');
        const insuranceCompanyQuery = shouldFilterForAdmin
          ? { document_types: ['hikiuke', 'handbook'] }
          : {};

        // Fetch all insurance companies (adminはhikiuke/handbook登録済みの会社のみ)
        const insuranceCompaniesResponse = await fetchInsuranceCompanies(insuranceCompanyQuery);
        const allCompanies = insuranceCompaniesResponse.map(company => ({
          name: company.name,
          nameKana: company.name_kana,
          indexName: company.index_name,
          insuranceType: company.insurance_type
        }));

        // 管理者は全件表示
        if (isAdmin) {
          setInsuranceCompanies(allCompanies);
        } else {
          // 代理店は許可された文書種別（approved_document_types）に基づいて絞り込み
          const agencyId = userInfo.agency_id;
          const agencyInsuranceCompaniesResponse = await fetchAgencyInsuranceCompanies(agencyId);

          // 承認済み文書種別を付与して保持
          const agencyCompaniesWithTypes = agencyInsuranceCompaniesResponse.map(agencyCompany => {
            // 保険会社マスタ情報と紐付け
            const fullCompanyInfo = insuranceCompaniesResponse.find(company => company.id === agencyCompany.id);
            return {
              id: agencyCompany.id,
              name: fullCompanyInfo.name,
              nameKana: fullCompanyInfo.name_kana,
              indexName: fullCompanyInfo.index_name,
              insuranceType: fullCompanyInfo.insurance_type,
              approved_document_types: agencyCompany.approved_document_types
            };
          });

          // 文書種別に応じて表示対象の保険会社を設定
          if (searchParams.documentType === DOCUMENT_TYPE.POLICY_BOOKLET) {
            setInsuranceCompanies(allCompanies);
          } else if (searchParams.documentType === DOCUMENT_TYPE.ALL) {
            // 「すべて」選択時は documentTypesAccess をもとに表示対象を決定する
            // ユーザーが明示的に「すべて」を選択した時点では documentTypesAccess はロード済みのため、ここで直接フィルタリングする
            const insuranceType = getInsuranceTypeFromTabName(activeTab);
            const filteredCompanies = getCompaniesForDocumentTypeAll({
              documentTypesAccess,
              agencyInsuranceCompanies: agencyCompaniesWithTypes,
              allInsuranceCompanies: allCompanies,
              insuranceType
            });
            setInsuranceCompanies(filteredCompanies);
          } else {
            // Apply filtering based on document type
            const filteredCompanies = getFilteredCompaniesByDocumentType(searchParams.documentType, agencyCompaniesWithTypes);
            setInsuranceCompanies(filteredCompanies);
          }
        }
      } catch (error) {
        // エラーダイアログ表示はBaseApiServiceで行う
        // エラーコンソール出力は各ApiServiceで行う
      } finally {
      }
    };

    fetchSetInsuranceCompanies();
  }, [activeTab, userInfo, searchParams.documentType]);

  /** ドキュメント種別コードの検証 */
  const mapDocumentTypeToKey = (docType) => {
    if (!docType) return '';
    const allowedCodes = new Set(Object.values(DOCUMENT_TYPE));
    return allowedCodes.has(docType) ? docType : '';
  };

  /** 代理店向け：文書種別に基づいて保険会社を絞り込む関数 */
  const getFilteredCompaniesByDocumentType = (docType, agencyCompanies) => {
    const documentTypeKey = mapDocumentTypeToKey(docType);

    if (!documentTypeKey) {
      return [];
    }

    // 代理店が許可された文書種別のみ残す
    return agencyCompanies
      .filter(company => company.approved_document_types.includes(documentTypeKey))
      .map(company => ({
        name: company.name,
        nameKana: company.nameKana,
        indexName: company.indexName,
        insuranceType: company.insuranceType
      }));
  };

  useEffect(() => {
    const insuranceType = getInsuranceTypeFromTabName(activeTab);
    let options = getOrderedDocumentTypes(documentTypesAccess, insuranceType);
    // DocumentTypeAll フラグが有効な場合のみ「すべて」エントリを合成して先頭に追加
    // バックエンドは all を返さないため、フロントエンドで合成する
    if (isFeatureEnabledDocumentTypeAll()) {
      const allEntry = { code: DOCUMENT_TYPE.ALL, name: 'すべて', insurance_type: insuranceType, is_enabled: hasEnabledNonDocumentTypeAll(options) };
      options = [allEntry, ...options];
    }
    // 先頭が '' でない場合は「指定なし」を先頭に追加する
    // 表示順: 指定なし → [すべて →] しおり・約款 / ...
    if (options.length > 0 && options[0].code !== '') {
      options.unshift({ code: '', name: '指定なし', is_enabled: true });
    }
    setDocumentTypes(options);

    // タブ切り替え時に文書種別をリセット
    setSearchParams(prev => ({ ...prev, insuranceType: getInsuranceTypeFromTabName(activeTab), documentType: '' }));
  }, [documentTypesAccess, activeTab]);

  useEffect(() => {
    setSearchParams(prev => ({ ...prev, insuranceCompanies: selectedInsuranceCompanies }));
  }, [selectedInsuranceCompanies]);

  useEffect(() => {
    setSearchParams(prev => ({ ...prev, productTypes: selectedProductTypes }));
  }, [selectedProductTypes]);

  /** Enterキーで検索をトリガーするハンドラー */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      onSearch();
    }
  };

  /** 検索条件リセット処理 */
  const handleReset = () => {
    onReset(getInsuranceTypeFromTabName(activeTab));

    setSelectedInsuranceCompanies([]);
    setSelectedProductTypes([]);
  };

  const onDocumentTypeChange = (documentType) => {
    if (documentType === DOCUMENT_TYPE.HIKIUKE) {
      setSelectedInsuranceCompanies([]);
      setSelectedProductTypes([]);
    }
    setSearchParams(prev => ({ ...prev, documentType }));
  }

  return (
    <aside className={`insurance-sidebar ${isMobile ? 'mobile' : ''} ${sidebarMinimized ? 'minimized' : ''} ${sidebarActive ? 'active' : ''}`}>
      {!sidebarMinimized ? (
        <>
          {/* PC sidebar toggle button */}
          {!isMobile && (
            <div className="sidebar-toggle-pc">
              <button className="sidebar-toggle-button-pc select-box ver02" onClick={toggleSidebarMinimized}>
                <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 2H10.5" stroke="#0073BC" />
                  <path d="M13.5 2H14.5" stroke="#0073BC" />
                  <circle cx="12" cy="2" r="1.5" stroke="#0073BC" />
                  <path d="M0 12L8.5 12" stroke="#0073BC" />
                  <path d="M11.5 12L14.5 12" stroke="#0073BC" />
                  <circle cx="10" cy="12" r="1.5" stroke="#0073BC" />
                  <path d="M0 7L4 7" stroke="#0073BC" />
                  <path d="M6.5 7H14.5" stroke="#0073BC" />
                  <circle cx="5" cy="7" r="1.5" stroke="#0073BC" />
                </svg>
                <span>閉じる</span>
              </button>
            </div>
          )}

          <div className="sidebar-header">
            検索履歴 検索条件
          </div>

          <div className={`sidebar-section ${isMobile ? 'mobile-safe-area' : ''}`}>
            <div className="document-section">

              <div className="date-selection">
                <div className="option-label">検索日時</div>
                <div className="date-range">
                  <DatePicker
                    value={searchParams.searchDateFrom}
                    onChange={(date) => setSearchParams(prev => ({ ...prev, searchDateFrom: date }))}
                    placeholder="開始日"
                  />
                  <span className="date-separator">〜</span>
                  <DatePicker
                    value={searchParams.searchDateTo}
                    onChange={(date) => setSearchParams(prev => ({ ...prev, searchDateTo: date }))}
                    placeholder="終了日"
                  />
                </div>
              </div>

              <div className='document-tabs-section'>
                <div className="search-tabs">
                  <button className={`tab-button-search ${activeTab === 'life' ? 'active' : ''}`} onClick={() => onTabChange('life')}>{getInsuranceTypeName(1)}</button>
                  <button className={`tab-button-search ${activeTab === 'sonpo' ? 'active' : ''}`} onClick={() => onTabChange('sonpo')}>{getInsuranceTypeName(2)}</button>
                </div>
              </div>

              {/* Document Type Selection */}
              <DocumentTypeSelect
                documentType={searchParams.documentType}
                setDocumentType={onDocumentTypeChange}
                insuranceType={getInsuranceTypeFromTabName(activeTab)}
                documentTypes={documentTypes}
                isHistory={true}
              />

              {/* Insurance Company Selection */}
              <InsuranceCompanySelect
                selectedInsuranceCompanies={selectedInsuranceCompanies}
                setSelectedInsuranceCompanies={setSelectedInsuranceCompanies}
                insuranceCompanies={insuranceCompanies}
                insuranceType={getInsuranceTypeFromTabName(activeTab)}
                documentType={searchParams.documentType}
                isHistory={true}
              />

              {/* Product Type Selection */}
              {searchParams.documentType !== DOCUMENT_TYPE.HIKIUKE && (
                <ProductTypeSelect
                  selectedProductTypes={selectedProductTypes}
                  setSelectedProductTypes={setSelectedProductTypes}
                  insuranceType={getInsuranceTypeFromTabName(activeTab)}
                />
              )}

              <DateRangePicker
                dateFrom={searchParams.dateFrom}
                setDateFrom={(dateFrom) => setSearchParams(prev => ({ ...prev, dateFrom }))}
                dateTo={searchParams.dateTo}
                setDateTo={(dateTo) => setSearchParams(prev => ({ ...prev, dateTo }))}
              />

              {/* Word Search */}
              <div className="word-search">
                <div className="option-label">
                  {searchParams.documentType === DOCUMENT_TYPE.HIKIUKE ? <p>傷病名検索</p> : <p>ワード検索</p>}
                </div>
                <div className="search-container-solicitor mt5">
                  <input
                    type="text"
                    value={searchParams.searchQuery}
                    onChange={(e) => setSearchParams(prev => ({ ...prev, searchQuery: e.target.value }))}
                    onKeyDown={handleKeyDown}
                    placeholder={activeTab === 'life' && searchParams.documentType === DOCUMENT_TYPE.HIKIUKE ? '例：心筋梗塞' : activeTab === 'life' ? '例：がん 診断 2回目' : '例：自動車 事故 対人'}
                    className="search-input"
                  />
                  <button className="search-button-solicitor-mini">
                    <svg width="17" height="18" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7.98828 9.84033L10.0072 8.0235L16.0914 14.7844C16.5931 15.3419 16.5478 16.2005 15.9903 16.7022Lnan nanLnan nanL15.9903 16.7022C15.4328 17.2039 14.5742 17.1587 14.0725 16.6012L7.98828 9.84033Z" fill="#3DA5E8" />
                      <path d="M14.0468 7.11545C14.0468 10.9643 10.9267 14.0844 7.07785 14.0844C3.229 14.0844 0.108887 10.9643 0.108887 7.11545C0.108887 3.2666 3.229 0.146484 7.07785 0.146484C10.9267 0.146484 14.0468 3.2666 14.0468 7.11545Z" fill="white" />
                      <path fillRule="evenodd" clipRule="evenodd" d="M7.07785 11.0844C9.26985 11.0844 11.0468 9.30745 11.0468 7.11545C11.0468 4.92345 9.26985 3.14648 7.07785 3.14648C4.88585 3.14648 3.10889 4.92345 3.10889 7.11545C3.10889 9.30745 4.88585 11.0844 7.07785 11.0844ZM7.07785 14.0844C10.9267 14.0844 14.0468 10.9643 14.0468 7.11545C14.0468 3.2666 10.9267 0.146484 7.07785 0.146484C3.229 0.146484 0.108887 3.2666 0.108887 7.11545C0.108887 10.9643 3.229 14.0844 7.07785 14.0844Z" fill="#0073BC" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="favorite-checkbox">
                <label className="checkbox-native-box">
                  <input
                    className="checkbox-native"
                    type="checkbox"
                    checked={searchParams.isFavorite}
                    onChange={(e) => setSearchParams(prev => ({ ...prev, isFavorite: e.target.checked }))}
                  />
                </label>
                <label>お気に入り</label>
              </div>

              {/* Search and Reset Buttons */}
              <div className={`button-container ${isMobile ? 'mobile-buttons' : ''}`}>
                <button
                  className={`search-btn ${isLoading ? 'loading' : ''}`}
                  onClick={onSearch}
                  disabled={isLoading}
                >
                  {isLoading ? '検索中...' : '検索'}
                </button>

                <button className="reset-btn" onClick={handleReset}>
                  条件をリセットする
                </button>
              </div>
            </div>
          </div>
        </>
      ) : (
        // Minimized sidebar display
        <div className="minimized-sidebar-content">
          <button className="sidebar-toggle-button-pc select-box ver03" onClick={toggleSidebarMinimized}>
            <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 2H10.5" stroke="#0073BC" />
              <path d="M13.5 2H14.5" stroke="#0073BC" />
              <circle cx="12" cy="2" r="1.5" stroke="#0073BC" />
              <path d="M0 12L8.5 12" stroke="#0073BC" />
              <path d="M11.5 12L14.5 12" stroke="#0073BC" />
              <circle cx="10" cy="12" r="1.5" stroke="#0073BC" />
              <path d="M0 7L4 7" stroke="#0073BC" />
              <path d="M6.5 7H14.5" stroke="#0073BC" />
              <circle cx="5" cy="7" r="1.5" stroke="#0073BC" />
            </svg>
            <span>開く</span>
          </button>
        </div>
      )}
    </aside>
  );
};

export default HistorySearchSidebar;